import { Controller } from '@hotwired/stimulus';
import { Modal } from 'bootstrap';

/**
 * Generic reusable Stimulus controller for Symfony form modals.
 *
 * Opens a URL in a Bootstrap modal, submits the Symfony form via fetch,
 * displays server-side validation errors inline, and reloads the target
 * section on success.
 *
 * Usage on a trigger button:
 *   data-action="modal-form#open"
 *   data-modal-form-url-param="/route/to/form"          (required)
 *   data-modal-form-title-param="New item"               (optional)
 *   data-modal-form-section-url-param="/route/section"  (optional — reload target)
 *   data-modal-form-section-target-param="#my-section"  (optional — CSS selector to replace)
 *   data-modal-form-reload-target-param="#my-datatable" (optional — dispatch DataTable reload)
 *   data-modal-form-size-param="modal-lg"               (optional — Bootstrap dialog size)
 */
export default class extends Controller {

    static targets = ['modal', 'modalTitle', 'modalBody', 'modalDialog'];

    declare readonly modalTarget: HTMLElement;
    declare readonly modalTitleTarget: HTMLElement;
    declare readonly modalBodyTarget: HTMLElement;
    declare readonly hasModalDialogTarget: boolean;
    declare readonly modalDialogTarget: HTMLElement;

    private bsModal!: Modal;
    private modalElement!: HTMLElement;
    private modalTitleElement!: HTMLElement;
    private modalBodyElement!: HTMLElement;
    private modalDialogElement?: HTMLElement;
    private openerElement?: HTMLElement;
    private reloadTargetSelector = '';
    private focusPending = false;
    private focusCallback: (() => void) | null = null;

    connect(): void {
        this.modalElement = this.modalTarget;
        this.modalTitleElement = this.modalTitleTarget;
        this.modalBodyElement = this.modalBodyTarget;
        this.modalDialogElement = this.hasModalDialogTarget ? this.modalDialogTarget : undefined;

        // Register pages wrap tab content in a parent form. Move the Bootstrap modal
        // outside that form before injecting AJAX forms to avoid invalid nested forms.
        if (this.modalElement.parentElement !== document.body) {
            document.body.appendChild(this.modalElement);
        }

        this.bsModal = new Modal(this.modalElement);
    }

    disconnect(): void {
        this.bsModal?.dispose();
        if (this.modalElement?.parentElement === document.body) {
            this.modalElement.remove();
        }
        this.cleanupModalArtifacts();
    }

    /** Opens the modal and fetches the form HTML from the server. */
    async open(event: Event): Promise<void> {
        event.preventDefault();
        const btn = event.currentTarget as HTMLElement;
        const url = btn.dataset.modalFormUrlParam;
        if (!url) return;
        this.openerElement = btn;

        const title       = btn.dataset.modalFormTitleParam ?? '';
        const sectionUrl  = btn.dataset.modalFormSectionUrlParam ?? '';
        const sectionSel  = btn.dataset.modalFormSectionTargetParam ?? '';
        const size        = btn.dataset.modalFormSizeParam ?? 'modal-lg';
        this.reloadTargetSelector = btn.dataset.modalFormReloadTargetParam ?? '';

        if (this.modalDialogElement) {
            this.modalDialogElement.className = `modal-dialog ${size}`;
        }

        this.modalTitleElement.textContent = title;
        this.modalBodyElement.innerHTML = `
            <div class="text-center py-4">
                <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Cargando...</span>
                </div>
            </div>`;

        // Track whether Bootstrap has finished its own focus handling.
        // shown.bs.modal fires AFTER _focusModal(), so we can safely override it then.
        this.focusPending = true;
        this.focusCallback = null;
        this.modalElement.addEventListener('shown.bs.modal', () => {
            this.focusPending = false;
            if (this.focusCallback) {
                this.focusCallback();
                this.focusCallback = null;
            }
        }, { once: true });

        this.bsModal.show();

        try {
            const res  = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
            const html = await res.text();
            this.modalBodyElement.innerHTML = html;
            this.bindForm(sectionUrl, sectionSel, url);
        } catch {
            this.modalBodyElement.innerHTML =
                `<div class="alert alert-danger m-3">Error al cargar el formulario.</div>`;
        }
    }

    /** Attaches the submit listener to the form inside the modal body. */
    private bindForm(sectionUrl: string, sectionSel: string, submitUrl = ''): void {
        const form = this.modalBodyElement.querySelector<HTMLFormElement>('form');
        if (!form) return;
        if (submitUrl && !form.getAttribute('action')) {
            form.action = submitUrl;
        }
        // Opt-out: if the inner form declares its own submit handling
        // (e.g. data-modal-form-skip-submit), don't double-bind submit.
        const skipSubmit = form.hasAttribute('data-modal-form-skip-submit');
        if (!skipSubmit) {
            form.addEventListener('submit', (e) => {
                e.preventDefault();
                void this.submitForm(form, sectionUrl, sectionSel);
            });
        }

        const firstInput = this.modalBodyElement.querySelector<HTMLElement>(
            'input:not([type="hidden"]):not([readonly]):not([disabled]), select:not([disabled]), textarea:not([disabled])'
        );
        if (firstInput) {
            const doFocus = (): void => { setTimeout(() => firstInput.focus(), 20); };
            if (this.focusPending) {
                // Form loaded before Bootstrap finished its animation; let shown.bs.modal call us.
                this.focusCallback = doFocus;
            } else {
                // Modal already fully open (e.g. re-bind after validation error).
                doFocus();
            }
        }
    }

    private async submitForm(
        form: HTMLFormElement,
        sectionUrl: string,
        sectionSel: string,
    ): Promise<void> {
        // Spinner on submit button
        const submitBtn = form.querySelector<HTMLButtonElement>('[type="submit"]');
        if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.innerHTML = `<span class="spinner-border spinner-border-sm me-1" role="status"></span> Guardando…`;
        }

        // Allow overriding the HTTP method via data-method on the <form> element,
        // since HTML forms only support GET / POST.
        const method = (form.dataset.method ?? form.method ?? 'POST').toUpperCase();

        try {
            this.syncFlatpickrInputs(form);

            const res = await fetch(form.action, {
                method,
                body: new FormData(form),
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });

            if (res.status === 422) {
                // Symfony returned the form with validation errors
                const html = await res.text();
                this.modalBodyElement.innerHTML = html;
                this.bindForm(sectionUrl, sectionSel, form.action);
                return;
            }

            if (res.ok) {
                const json = await res.json();
                if (json.success) {
                    // Notify parent controllers / listeners
                    this.dispatch('success', { detail: json, bubbles: true });
                    await this.hideModal();
                    // Reload the configured section
                    if (sectionUrl && sectionSel) {
                        await this.reloadSection(sectionUrl, sectionSel);
                    } else {
                        this.reloadSourceDatatable();
                    }
                    this.showSuccess(json.message ?? 'Guardado correctamente.');
                }
            }
        } catch (err) {
            console.error('[modal-form] submit error:', err);
            if (submitBtn) {
                submitBtn.disabled = false;
                submitBtn.textContent = 'Guardar';
            }
        }
    }

    /** Fetches fresh section HTML and replaces the matching element in the DOM. */
    private async reloadSection(url: string, selector: string): Promise<void> {
        try {
            const res  = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
            const html = await res.text();
            const target = document.querySelector(selector);
            if (target) target.outerHTML = html;
            this.cleanupModalArtifacts();
        } catch (err) {
            console.error('[modal-form] reload section error:', err);
        }
    }

    private hideModal(): Promise<void> {
        if (!this.modalElement.classList.contains('show')) {
            this.cleanupModalArtifacts();
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            const done = (): void => {
                this.modalElement.removeEventListener('hidden.bs.modal', done);
                this.cleanupModalArtifacts();
                resolve();
            };

            this.modalElement.addEventListener('hidden.bs.modal', done, { once: true });
            this.bsModal.hide();
        });
    }

    private cleanupModalArtifacts(): void {
        document.querySelectorAll('.modal-backdrop').forEach((backdrop) => backdrop.remove());
        document.body.classList.remove('modal-open');
        document.body.style.removeProperty('overflow');
        document.body.style.removeProperty('padding-right');
    }

    private reloadSourceDatatable(): void {
        const explicitDatatable = this.reloadTargetSelector
            ? document.querySelector<HTMLElement>(this.reloadTargetSelector)
            : null;
        const rowDatatable = this.openerElement?.closest<HTMLElement>('[data-controller~="datatable"]');
        const tabDatatable = this.openerElement
            ?.closest<HTMLElement>('.tab-pane')
            ?.querySelector<HTMLElement>('[data-controller~="datatable"]');
        const datatable = explicitDatatable ?? rowDatatable ?? tabDatatable;

        datatable?.dispatchEvent(new CustomEvent('bulk-actions:reload', { bubbles: true }));
    }

    private showSuccess(message: string): void {
        const toastr = (window as any).toastr;
        if (toastr?.success) {
            toastr.success(message);
        }
    }

    private syncFlatpickrInputs(form: HTMLFormElement): void {
        form.querySelectorAll<HTMLInputElement>('input').forEach((input) => {
            const flatpickr = (input as any)._flatpickr;
            if (!flatpickr) return;

            const altValue = flatpickr.altInput?.value;
            if (flatpickr.altInput && !altValue) {
                input.value = '';
                return;
            }

            const selected = flatpickr.selectedDates?.[0];
            if (selected) {
                input.value = flatpickr.formatDate(selected, flatpickr.config.dateFormat);
                return;
            }

            if (!altValue) {
                input.value = '';
                return;
            }

            const parsed = flatpickr.parseDate(altValue, flatpickr.config.altFormat);
            if (parsed) {
                input.value = flatpickr.formatDate(parsed, flatpickr.config.dateFormat);
            }
        });
    }
}
